import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Button, Modal, Box, Typography, Grid, Stack, IconButton } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { tokens } from "../../theme";
import { Add, Remove } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { listAllActivities, resetAllActivities, updateActivitiesForBookings } from "../../store/actions";

const AddUpdateActivitiesButton = ({ bookingDetails}) => {
  const [open, setOpen] = useState(false);
  const [activities, setActivities] = useState({});
  
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const dispatch = useDispatch();

  const { activitiesList } = useSelector(({ activities }) => ({
    activitiesList: activities?.activities?.list || [],
  }));

  // Fetch activities on mount
  useEffect(() => {
    dispatch(resetAllActivities());
    dispatch(listAllActivities());
  }, [dispatch]);

  // Initialize activities state when activities list is loaded
  useEffect(() => {
    if (!activitiesList.length) return;

    // Filter out inactive activities and create initial state
    const activeActivities = activitiesList.filter((activity) => activity.isActive !== false);
    
    const initialActivities = {};
    activeActivities.forEach((activity) => {
      initialActivities[activity._id] = {
        id: activity._id,
        name: activity.name,
        description: activity.description,
        price: activity.rate,
        count: 0,
        total: 0,
        isSelected: false,
      };
    });

    // Update with booking details if available
    if (bookingDetails?.activities?.length) {
      bookingDetails.activities.forEach((bookingActivity) => {
        if (initialActivities[bookingActivity.id] && bookingActivity.count > 0) {
          const activity = initialActivities[bookingActivity.id];
          activity.count = bookingActivity.count;
          activity.total = activity.price * bookingActivity.count;
          activity.isSelected = true;
        }
      });
    }

    setActivities(initialActivities);
  }, [activitiesList, bookingDetails]);

  // Memoized selected and available activities
  const { selectedActivities, availableActivities, grandTotal } = useMemo(() => {
    const activitiesArray = Object.values(activities);
    const selected = activitiesArray.filter((act) => act.isSelected);
    const available = activitiesArray.filter((act) => !act.isSelected);
    const total = selected.reduce((sum, act) => sum + act.total, 0);

    return {
      selectedActivities: selected,
      availableActivities: available,
      grandTotal: total,
    };
  }, [activities]);

  // Handle increment
  const handleIncrement = useCallback((activityId) => {
    setActivities((prev) => {
      const activity = prev[activityId];
      if (!activity) return prev;

      const newCount = activity.count + 1;
      const newTotal = activity.price * newCount;

      return {
        ...prev,
        [activityId]: {
          ...activity,
          count: newCount,
          total: newTotal,
          isSelected: true,
        },
      };
    });
  }, []);

  // Handle decrement
  const handleDecrement = useCallback((activityId) => {
    setActivities((prev) => {
      const activity = prev[activityId];
      if (!activity || activity.count <= 0) return prev;

      const newCount = activity.count - 1;
      const newTotal = activity.price * newCount;
      const isSelected = newCount > 0;

      return {
        ...prev,
        [activityId]: {
          ...activity,
          count: newCount,
          total: newTotal,
          isSelected,
        },
      };
    });
  }, []);

  const handleSave = useCallback(() => {
    // Extract only selected activities with count > 0
    const activitiesToSave = selectedActivities.map((activity) => ({
      id: activity.id,
      name: activity.name,
      description: activity.description,
      rate: activity.price * activity.count,
      count: activity.count,
    }));
    const data = {
        bookingId: bookingDetails.booking_id,
        activities: activitiesToSave,
    };
    console.log("Saved activities:", activitiesToSave);
    dispatch(updateActivitiesForBookings(data));
    
    setOpen(false);
  }, [selectedActivities]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 800,
    maxHeight: "80vh",
    overflowY: "auto",
    bgcolor: colors.primary[400],
    outline: "none",
    boxShadow: 24,
    p: 4,
    borderRadius: 2,
  };

  return (
    <>
      <Button size="small" variant="outlined" onClick={() => setOpen(true)}>
        <b>Add/Update Activities</b>
      </Button>

      <Modal open={open} onClose={handleClose}>
        <Box sx={modalStyle}>
          <Typography variant="h6" mb={2}>
            Manage Activities
          </Typography>

          {/* Selected Activities Section */}
          {selectedActivities.length > 0 && (
            <>
              <Typography fontWeight={600} mb={1}>
                Selected Activities
              </Typography>
              <Grid container spacing={2} mb={3}>
                {selectedActivities.map((activity) => (
                  <Grid item xs={12} sm={6} md={4} key={activity.id}>
                    <Stack
                      p={2}
                      borderRadius={1}
                      bgcolor={colors.primary[500]}
                      spacing={1}
                      border={`2px solid ${colors.greenAccent[500]}`}
                    >
                      <Typography fontWeight={600}>{activity.name}</Typography>
                      
                      {activity.description && (
                        <Typography 
                          variant="body2" 
                          color={colors.grey[200]}
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={activity.description}
                        >
                          {activity.description}
                        </Typography>
                      )}
                      
                      <Typography color={colors.greenAccent[400]}>
                        ₹{activity.price} × {activity.count} = ₹{activity.total}
                      </Typography>

                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" mt={1}>
                        <IconButton
                          size="small"
                          onClick={() => handleDecrement(activity.id)}
                          aria-label={`Decrease ${activity.name} count`}
                          disabled={activity.count <= 0}
                        >
                          <Remove fontSize="small" />
                        </IconButton>
                        
                        <Typography 
                          fontWeight={600} 
                          minWidth={40} 
                          textAlign="center"
                          sx={{ 
                            px: 1,
                            py: 0.5,
                            bgcolor: colors.primary[400],
                            borderRadius: 1,
                          }}
                        >
                          {activity.count}
                        </Typography>
                        
                        <IconButton
                          size="small"
                          onClick={() => handleIncrement(activity.id)}
                          aria-label={`Increase ${activity.name} count`}
                        >
                          <Add fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Stack>
                  </Grid>
                ))}
              </Grid>

              {/* Grand Total */}
              <Box 
                p={2} 
                bgcolor={colors.primary[500]} 
                borderRadius={1}
                mb={2}
                border={`1px solid ${colors.greenAccent[500]}`}
              >
                <Typography variant="h6" color={colors.greenAccent[400]} fontWeight={700}>
                  Grand Total: ₹{grandTotal}
                </Typography>
              </Box>
            </>
          )}

          {/* Available Activities Section */}
          {availableActivities.length > 0 && (
            <>
              <Typography fontWeight={600} mb={1}>
                Available Activities
              </Typography>
              <Grid container spacing={2}>
                {availableActivities.map((activity) => (
                  <Grid item xs={12} sm={6} md={4} key={activity.id}>
                    <Stack
                      p={2}
                      borderRadius={1}
                      bgcolor={colors.primary[500]}
                      spacing={1}
                      border={`1px solid ${colors.grey[700]}`}
                    >
                      <Typography fontWeight={600}>{activity.name}</Typography>
                      
                      {activity.description && (
                        <Typography 
                          variant="body2" 
                          color={colors.grey[200]}
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={activity.description}
                        >
                          {activity.description}
                        </Typography>
                      )}
                      
                      <Typography color={colors.greenAccent[400]}>
                        ₹{activity.price}
                      </Typography>

                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" mt={1}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Add />}
                          onClick={() => handleIncrement(activity.id)}
                          aria-label={`Add ${activity.name}`}
                        >
                          Add
                        </Button>
                      </Stack>
                    </Stack>
                  </Grid>
                ))}
              </Grid>
            </>
          )}

          {/* Empty State */}
          {Object.keys(activities).length === 0 && (
            <Typography color={colors.grey[300]} textAlign="center" py={4}>
              No activities available
            </Typography>
          )}

          {/* Action Buttons */}
          <Stack mt={3} direction="row" justifyContent="flex-end" spacing={2}>
            <Button variant="outlined" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="secondary"
            //   disabled={selectedActivities.length === 0}
              onClick={handleSave}
            >
              <b>Save Activities ({selectedActivities.length})</b>
            </Button>
          </Stack>
        </Box>
      </Modal>
    </>
  );
};

export default AddUpdateActivitiesButton;